import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import { getAdminChannels, getPlansForChannel, manualAddUserToChannel, getChannelDetails } from "../../../lib/apiClient";
import { Channel, Plan } from "../../../types";

const AdminManualAddUserPage: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [selectedChannelId, setSelectedChannelId] = useState<string>("");
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [referralCode, setReferralCode] = useState<string>("");

  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [referralCodes, setReferralCodes] = useState<string[]>([]);
  const [referralCodesLoading, setReferralCodesLoading] = useState(false);
  const [referralCodesError, setReferralCodesError] = useState<string | null>(null);

  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isskipKyc, setskipKyc] = useState(false);
  const channelOptions = useMemo(
    () => channels.map((c) => ({ value: c._id, label: c.name })),
    [channels]
  );
  const planOptions = useMemo(
    () => plans.map((p) => ({ value: p._id, label: p.name })),
    [plans]
  );
  const referralCodeOptions = useMemo(
    () => referralCodes.map((code) => ({ value: code, label: code })),
    [referralCodes]
  );

  const fetchChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelsError(null);
    try {
      const list = await getAdminChannels();
      setChannels(list as unknown as Channel[]);
      // Auto-select a channel to unlock plan selection
      if (Array.isArray(list) && list.length > 0) {
        const first = (list[0] as any)?._id;
        if (first) {
          setSelectedChannelId(first as string);
        }
      }
    } catch (e: any) {
      setChannelsError(e?.message || "Failed to load channels");
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async (channelId: string) => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const list = await getPlansForChannel(channelId);
      setPlans(list);
      if (Array.isArray(list)) {
        if (list.length === 0) {
          setPlansError("No plans found for this channel");
          setSelectedPlanId("");
        } else if (list.length === 1) {
          setSelectedPlanId((list[0] as any)?._id || "");
        }
      }
    } catch (e: any) {
      setPlansError(e?.message || "Failed to load plans for channel");
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const fetchReferralCodes = useCallback(async (channelId: string) => {
    setReferralCodesLoading(true);
    setReferralCodesError(null);
    try {
      const channel = await getChannelDetails(channelId as string);
      const codes = (channel as any)?.referralCode || [];
      setReferralCodes(Array.isArray(codes) ? codes : []);
      // Auto-select first code if available
      if (Array.isArray(codes) && codes.length > 0) {
        setReferralCode((prev) => prev || (codes[0] as string));
      } else {
        setReferralCode("");
      }
    } catch (e: any) {
      setReferralCodesError(e?.message || "Failed to load referral codes");
      setReferralCodes([]);
      setReferralCode("");
    } finally {
      setReferralCodesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  useEffect(() => {
    if (selectedChannelId) {
      setSelectedPlanId("");
      fetchPlans(selectedChannelId);
      fetchReferralCodes(selectedChannelId);
    } else {
      setPlans([]);
      setReferralCodes([]);
      setReferralCode("");
    }
  }, [selectedChannelId, fetchPlans, fetchReferralCodes]);

  const validate = (): boolean => {
    const errs: string[] = [];
    const phoneDigits = phone.replace(/\D/g, "");
    if (!phoneDigits || phoneDigits.length < 10) errs.push("Enter a valid phone number");
    if (!selectedChannelId) errs.push("Select a channel");
    if (!selectedPlanId) errs.push("Select a plan");
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt < 0) errs.push("Enter a valid amount");
    if (!referralCode.trim()) errs.push("Referral code is required");
    setFormError(errs[0] || null);
    return errs.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        phone: phone.replace(/\D/g, ""),
        channel_id: selectedChannelId,
        plan_id: selectedPlanId,
        amount: Number(amount),
        should_kyc: !isskipKyc,
      };
      const res = await manualAddUserToChannel(payload);
      setSuccessMessage(`User added. Invite sent: ${res.whatsappSent ? "Yes" : "No"}`);
      setIsSubmitting(false);
    } catch (e: any) {
      setFormError(e?.message || "Failed to add user");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <PageTitle title="Manual Add to Premium Channels" subtitle="Add an existing/new user directly to a paid channel." />

      <form
        onSubmit={handleSubmit}
        className="bg-gradient-to-br from-dark-secondary via-dark-secondary to-dark-tertiary/20 p-8 rounded-xl shadow-2xl border border-dark-tertiary/60 backdrop-blur-sm"
      >
        <div className="space-y-6">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">1</span>
              </div>
              User Information
            </h2>
            <p className="text-text-secondary text-sm ml-11">Enter the user's phone number</p>
          </div>

          <div className="ml-11">
            <Input
              label="Phone Number"
              id="manual_phone"
              name="manual_phone"
              placeholder="e.g. 9876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">2</span>
              </div>
              Selection
            </h2>
            <p className="text-text-secondary text-sm ml-11">Choose channel and plan</p>
          </div>

          <div className="ml-11 space-y-6">
            <Select
              label="Channel"
              placeholder="Select a channel"
              options={channelOptions}
              value={selectedChannelId}
              onChange={(e) => setSelectedChannelId(e.target.value)}
              isLoading={channelsLoading}
              disabled={channelsLoading || isSubmitting}
              error={channelsError || undefined}
            />

            <Select
              label="Plan"
              placeholder={!selectedChannelId ? "Select channel first" : plans.length ? "Select a plan" : "No plans found"}
              options={planOptions}
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              isLoading={plansLoading}
              disabled={!selectedChannelId || plansLoading || isSubmitting}
              error={plansError || undefined}
            />
          </div>
        </div>

        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">3</span>
              </div>
              Payment
            </h2>
            <p className="text-text-secondary text-sm ml-11">Enter the payment amount</p>
          </div>

          <div className="ml-11 max-w-md">
            <Input
              label="Amount"
              id="manual_amount"
              name="manual_amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={0}
              step={1}
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">4</span>
              </div>
              Referral
            </h2>
            <p className="text-text-secondary text-sm ml-11">Select a referral code if available</p>
          </div>

          <div className="ml-11 max-w-lg">
            <Select
              label="Referral Code"
              placeholder={!selectedChannelId ? "Select channel first" : referralCodeOptions.length ? "Select a referral code" : "No codes available"}
              options={referralCodeOptions}
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              isLoading={referralCodesLoading}
              disabled={!selectedChannelId || referralCodesLoading || isSubmitting}
              error={referralCodesError || undefined}
            />
          </div>
        </div>
        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-golden-accent font-bold text-sm">5</span>
                </div>
                <h2 className="text-xl font-semibold text-text-primary">KYC Details</h2>
              </div>

              {/* Checkbox grouped with label */}
              <label
                htmlFor="should-skip-kyc"
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="should-skip-kyc"
                  className="form-checkbox h-4 w-4 text-golden-accent rounded focus:ring-golden-focus-ring bg-dark-tertiary border-dark-border checked:bg-golden-accent checked:border-transparent focus:ring-offset-dark-primary"
                  checked={isskipKyc}
                  onChange={(e) => setskipKyc(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="text-sm text-text-secondary">Skip KYC</span>
              </label>
            </div>

            <p className="text-text-secondary text-sm ml-11">
              Skipping KYC will allow the user to access the channel immediately
              without completing KYC verification. Use this option only for trusted
              users.
            </p>
          </div>
        </div>

        {formError && (
          <div className="mt-8">
            <ErrorMessage message={formError} />
          </div>
        )}
        {successMessage && (
          <div className="mt-4 p-3 rounded bg-green-900/30 border border-green-700 text-green-300">
            {successMessage}
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-center">
          <Button type="button" variant="secondary" onClick={() => {
            setPhone("");
            setSelectedChannelId("");
            setSelectedPlanId("");
            setAmount("");
            setReferralCode("");
            setskipKyc(false);
            setFormError(null);
            setSuccessMessage(null);
          }} disabled={isSubmitting}>Clear</Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting} disabled={isSubmitting}>Add User</Button>
        </div>
      </form>

      {(channelsLoading || plansLoading) && (
        <div className="mt-6"><LoadingIndicator text="Loading data..." /></div>
      )}
    </div>
  );
};

export default AdminManualAddUserPage;
